import type {
  ArticleState,
  StateScenario,
  StateScenarioCategory,
} from "./types";

const SEMANTIC_DRAFT =
  "https://datatracker.ietf.org/doc/html/draft-jurkovikj-http-semantic-validator-01";
const AST_DRAFT =
  "https://datatracker.ietf.org/doc/html/draft-jurkovikj-httpapi-agentic-state-02";

export const INITIAL_ARTICLE_STATE: ArticleState = Object.freeze({
  revision: 7,
  title: "Example",
  status: "published",
  sbrEtag: '"json-v18"',
  projectionEtag: '"html-v52"',
  semanticEtag: '"article-state-v7"',
});

export const NEXT_ARTICLE_STATE: ArticleState = Object.freeze({
  revision: 8,
  title: "Example",
  status: "draft",
  sbrEtag: '"json-v19"',
  projectionEtag: '"html-v53"',
  semanticEtag: '"article-state-v8"',
});

export const INTERVENING_ARTICLE_STATE: ArticleState = Object.freeze({
  revision: 9,
  title: "Example revised elsewhere",
  status: "published",
  sbrEtag: '"json-v20"',
  projectionEtag: '"html-v54"',
  semanticEtag: '"article-state-v9"',
});

export const STATE_CATEGORY_LABELS: Readonly<
  Record<StateScenarioCategory, string>
> = {
  validator_scope: "Validator scope",
  ast_core: "AST Core",
  ast_semantic: "AST Semantic",
  atomicity: "Atomic compare-and-commit",
};

export const STATE_SCENARIOS: readonly StateScenario[] = [
  {
    id: "validator-scope",
    title: "One state, different representation ETags",
    category: "validator_scope",
    kind: "observe",
    summary:
      "The HTML projection and JSON SBR have different ordinary ETags while exposing one semantic validator for the same logical state.",
    rule:
      "ETag validates a selected representation. Semantic-ETag identifies server-defined logical state and is not a cache validator.",
    references: [
      {
        label: "Semantic Validators §3",
        href: `${SEMANTIC_DRAFT}#section-3`,
      },
      {
        label: "AST §5.1",
        href: `${AST_DRAFT}#section-5.1`,
      },
    ],
  },
  {
    id: "core-success",
    title: "AST Core commits at the SBR",
    category: "ast_core",
    kind: "mutation",
    summary:
      "The client mutates the State-Bearing Resource with the strong ETag obtained from that same selected representation.",
    rule:
      "AST Core keeps ordinary If-Match semantics: target the SBR, select the SBR, and compare its current strong ETag atomically.",
    references: [
      {
        label: "AST §4.3",
        href: `${AST_DRAFT}#section-4.3`,
      },
      {
        label: "AST §6.2",
        href: `${AST_DRAFT}#section-6.2`,
      },
    ],
    mutation: {
      mode: "core",
      target: "sbr",
      capabilityKnown: true,
      precondition: { field: "If-Match", value: '"json-v18"' },
    },
  },
  {
    id: "core-wrong-target",
    title: "An SBR ETag cannot move to a projection",
    category: "ast_core",
    kind: "mutation",
    summary:
      "The client sends the SBR's ETag as If-Match to the HTML resource, which does not implement an unsafe AST Semantic method.",
    rule:
      "An ordinary ETag from one target cannot be reinterpreted as an If-Match condition on another target.",
    references: [
      {
        label: "AST §4.4",
        href: `${AST_DRAFT}#section-4.4`,
      },
    ],
    mutation: {
      mode: "core",
      target: "projection",
      capabilityKnown: false,
      precondition: { field: "If-Match", value: '"json-v18"' },
    },
  },
  {
    id: "missing-precondition",
    title: "Protected writes require an observed version",
    category: "ast_core",
    kind: "mutation",
    summary:
      "A protected mutation reaches the SBR without the required version-specific If-Match field.",
    rule:
      "After normal validation, an absent required precondition produces 428 and the state is not changed.",
    references: [
      {
        label: "AST §6.1",
        href: `${AST_DRAFT}#section-6.1`,
      },
    ],
    mutation: {
      mode: "core",
      target: "sbr",
      capabilityKnown: true,
    },
  },
  {
    id: "semantic-success",
    title: "AST Semantic crosses a representation boundary",
    category: "ast_semantic",
    kind: "mutation",
    summary:
      "After positive capability discovery, a client that observed the HTML projection safely mutates through that projected target.",
    rule:
      "If-Semantic-Match is evaluated in the advertised concurrency domain; successful compare, transition, and validator publication are atomic.",
    references: [
      {
        label: "Semantic Validators §4",
        href: `${SEMANTIC_DRAFT}#section-4`,
      },
      {
        label: "AST §5.2",
        href: `${AST_DRAFT}#section-5.2`,
      },
    ],
    mutation: {
      mode: "semantic",
      target: "projection",
      capabilityKnown: true,
      precondition: {
        field: "If-Semantic-Match",
        value: '"article-state-v7"',
      },
    },
  },
  {
    id: "semantic-capability-unknown",
    title: "A semantic validator is not capability proof",
    category: "ast_semantic",
    kind: "mutation",
    summary:
      "The projection exposes Semantic-ETag but does not positively advertise enforcement for this target and PATCH method.",
    rule:
      "The client must not rely on an extension precondition that an unaware server could silently ignore, so no request is sent.",
    references: [
      {
        label: "Semantic Validators §4.1",
        href: `${SEMANTIC_DRAFT}#section-4.1`,
      },
      {
        label: "AST §5.3",
        href: `${AST_DRAFT}#section-5.3`,
      },
    ],
    mutation: {
      mode: "semantic",
      target: "projection",
      capabilityKnown: false,
      precondition: {
        field: "If-Semantic-Match",
        value: '"article-state-v7"',
      },
    },
  },
  {
    id: "semantic-stale",
    title: "A stale semantic condition fails closed",
    category: "ast_semantic",
    kind: "mutation",
    summary:
      "Another writer commits revision 9 after the client observes revision 7 but before its conditional mutation is compared.",
    rule:
      "A false version-specific semantic condition produces 412. A returned current validator is advisory, not permission for blind retry.",
    references: [
      {
        label: "Semantic Validators §4.5",
        href: `${SEMANTIC_DRAFT}#section-4.5`,
      },
      {
        label: "AST §6.4",
        href: `${AST_DRAFT}#section-6.4`,
      },
    ],
    mutation: {
      mode: "semantic",
      target: "projection",
      capabilityKnown: true,
      precondition: {
        field: "If-Semantic-Match",
        value: '"article-state-v7"',
      },
      interveningState: INTERVENING_ARTICLE_STATE,
    },
  },
  {
    id: "atomic-two-writers",
    title: "Two writers observe; only one commits",
    category: "atomicity",
    kind: "concurrent",
    summary:
      "Two clients construct writes against the same SBR ETag. Writer A commits first; Writer B then compares against the new current state.",
    rule:
      "All write paths participate in one atomic compare-and-commit boundary, so both requests cannot commit against the same old validator.",
    references: [
      {
        label: "Semantic Validators §4.3",
        href: `${SEMANTIC_DRAFT}#section-4.3`,
      },
      {
        label: "AST §6.2",
        href: `${AST_DRAFT}#section-6.2`,
      },
    ],
  },
];

export function findStateScenario(id: string): StateScenario | undefined {
  return STATE_SCENARIOS.find((scenario) => scenario.id === id);
}
